import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  GraduationCap, Users, Home as HomeIcon, Crown, Building2, Smartphone, ClipboardCheck,
  Mail, Activity, Calendar, FileSpreadsheet, BarChart3, Bell, LayoutGrid, Check, ArrowRight,
} from "lucide-react";
import { PageHero, CTASection } from "../components/site/Shared";
import { Reveal } from "../components/site/Reveal";
import { PccMockup, HealthDot, AhiRadial, ProgressBar, MiniBars, MiniArea, KpiCard } from "../components/site/Mockups";
import { ParentAppShowcase } from "../components/site/ParentApp";
import { TeacherAppShowcase } from "../components/site/TeacherApp";
import { DemoPreview, RoleDemoCTA } from "../components/site/DemoPreview";

/* Rich recreated product visuals for key capabilities */
const capVisuals = {
  ahi: (
    <div className="rounded-2xl bg-[#0E1B2A] border border-white/10 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-white/50 text-[10px]">AHI Score · School</div>
          <div className="font-mono-accent text-3xl font-semibold text-white mt-0.5">85.6</div>
          <div className="text-[#22C55E] text-[10px] mt-1 flex items-center gap-1"><HealthDot /> Good Health</div>
        </div>
        <AhiRadial size={92} />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        {[["Student AHI", 78, "warn"], ["Class AHI", 66, "bad"], ["Teacher AHI", 89, "good"], ["School AHI", 86, "good"]].map((r) => (
          <div key={r[0]} className="bg-white/5 rounded-lg px-2.5 py-2">
            <div className="flex justify-between text-[9px] mb-1"><span className="text-white/60">{r[0]}</span><span className="font-mono-accent text-white">{r[1]}%</span></div>
            <ProgressBar value={r[1]} tone={r[2]} />
          </div>
        ))}
      </div>
    </div>
  ),
  pcc: <PccMockup />,
  examination: (
    <div className="rounded-2xl bg-white border border-[#ECECE8] p-4">
      <div className="grid grid-cols-3 gap-2">
        <KpiCard icon={FileSpreadsheet} label="Pass Rate" value="85%" sub="+5%" trend="up" />
        <KpiCard icon={Activity} label="At-Risk" value="18" sub="+4" trend="down" />
        <KpiCard icon={GraduationCap} label="Growth" value="72%" />
      </div>
      <div className="mt-2.5 bg-white rounded-xl border border-[#ECECE8] p-3">
        <div className="text-[10px] font-semibold text-[#1A1D26] mb-2">Class Passing Rate</div>
        <MiniBars />
      </div>
    </div>
  ),
  analytics: (
    <div className="rounded-2xl bg-white border border-[#ECECE8] p-4">
      <div className="text-[10px] font-semibold text-[#1A1D26] mb-2">School-wide teaching adherence</div>
      <MiniArea color="#5B7CFA" />
    </div>
  ),
};

/* ---- Stakeholders ---- */
const stakeholders = [
  {
    key: "teacher", label: "Teacher", icon: Smartphone, tagline: "Teachers operate.",
    desc: "The PARTECON Teacher App brings the daily classroom workflow into one connected experience — and every action feeds the school's real-time operating layer.",
    items: ["Class schedule & ongoing classes", "Attendance & lesson logging", "End-of-Class Summary (EOC)", "Assignments & evaluation", "Behaviour feedback & Teacher AHI", "Communication via PCC"],
    visual: <div className="space-y-5"><TeacherAppShowcase limit={3} /><RoleDemoCTA role="Teacher" cta="Explore the full Teacher Experience" /></div>,
  },
  {
    key: "student", label: "Student", icon: GraduationCap, tagline: "Students participate.",
    desc: "Students stay connected to their academic environment — schedules, homework, exams, performance and communication in one place.",
    items: ["Classes & schedules", "Homework & academic activities", "Examination information", "Performance & Student AHI", "Communication via PCC", "Calendar, events & notifications"],
    visual: <DemoPreview role="Student" title="Explore the Student Experience" cta="Explore the Student Experience"
      blurb="A connected experience for schedules, homework, exams, performance and communication — see the full Student App in a personalised demo." />,
  },
  {
    key: "parent", label: "Parent", icon: HomeIcon, tagline: "Parents stay connected.",
    desc: "Everything parents need from the school, connected in one place — a dedicated Parent App on the same governed operating layer, with attendance, AHI, exams, calendar and communication.",
    items: ["Attendance visibility", "AHI — Analytical Health Index", "Examination progress & results", "Calendar & school events", "Notices, circulars & PCC", "Notifications & relevant updates"],
    visual: <ParentAppShowcase />,
  },
  {
    key: "principal", label: "Principal", icon: Crown, tagline: "Principals see.",
    desc: "The Principal Command Center gives leadership a central, real-time view of what is happening across the school — and what needs attention now.",
    items: ["Classes running & ongoing classes", "Attendance & EOC compliance", "AHI & tasks requiring attention", "Teacher / student / class performance", "PCC escalations & operational alerts", "Systemic performance concerns"],
    visual: <DemoPreview role="Principal" title="Explore the Principal Dashboard" cta="Explore the Principal Dashboard"
      blurb="School-wide real-time visibility — AHI, attendance, EOC compliance, performance, alerts and the tasks that need attention. See the full Command Center in a personalised demo." />,
  },
  {
    key: "admin", label: "Administration", icon: Building2, tagline: "Administrators manage.",
    desc: "Administration operates from the same underlying PARTECON system — centralising school operations with role-based, permissioned access.",
    items: ["User management & roles", "Central & school administration", "Permission-controlled access", "Activity logs & records", "Operational workflows & settings", "Centralised school data"],
    visual: <DemoPreview role="Administration" title="Explore the Administration Experience" cta="Explore the Administration Experience"
      blurb="The administrative operating layer — records, workflows, communication, scheduling, reporting and school-wide coordination. See the full Administration console in a personalised demo." />,
  },
];

const StakeholderExplorer = () => {
  const [active, setActive] = useState("teacher");
  const s = stakeholders.find((x) => x.key === active);
  return (
    <section className="canvas py-20 md:py-28" data-testid="offer-stakeholders">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <span className="overline">What each stakeholder gets</span>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-[#1A1D26] leading-tight">A dedicated experience for every role.</h2>
          <p className="text-[#6B7280] text-base md:text-lg leading-relaxed mt-5">Each stakeholder gets their own experience — all built on the same connected operating layer.</p>
        </Reveal>
        <div className="flex flex-wrap gap-2 mt-8">
          {stakeholders.map((x) => (
            <button key={x.key} onClick={() => setActive(x.key)} data-testid={`offer-stakeholder-${x.key}`}
              className={`inline-flex items-center gap-2 text-[13px] font-medium rounded-lg px-4 py-2.5 border transition-colors ${active === x.key ? "bg-[#6366F1] text-white border-[#6366F1]" : "bg-white text-[#6B7280] border-[#ECECE8] hover:text-[#1A1D26]"}`}>
              <x.icon size={15} /> {x.label}
            </button>
          ))}
        </div>
        <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
          className="grid lg:grid-cols-2 gap-8 items-center mt-10">
          <div>
            <span className="overline">{s.tagline}</span>
            <h3 className="font-display text-2xl font-semibold text-[#1A1D26] mt-3">For {s.key === "admin" ? "Administration" : `${s.label}s`}</h3>
            <p className="text-[#6B7280] text-[15px] leading-relaxed mt-3">{s.desc}</p>
            <div className="grid sm:grid-cols-2 gap-2.5 mt-6">
              {s.items.map((it) => (
                <div key={it} className="flex items-center gap-2.5 text-[14px] text-[#1A1D26]">
                  <span className="h-5 w-5 rounded-full bg-[#DCFCE7] flex items-center justify-center shrink-0"><Check size={12} className="text-[#16A34A]" /></span>{it}
                </div>
              ))}
            </div>
            <Link to={`/solutions/${s.key}`} data-testid={`offer-solution-link-${s.key}`}
              className="inline-flex items-center gap-2 mt-7 text-[14px] font-semibold text-[#4F46E5] hover:gap-3 transition-all">
              See the {s.label} solution <ArrowRight size={15} />
            </Link>
          </div>
          <div>{s.visual}</div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---- The Operating Pillars ---- */
const pillarsData = [
  {
    key: "academic", icon: GraduationCap, tone: "#6366F1", label: "Academic Execution",
    tagline: "How learning is delivered, captured and closed — every day.",
    ops: ["Class execution", "Attendance", "Assignments & evaluation", "Examinations", "Lesson plans & to-do"],
    primitive: { name: "EOC", full: "End-of-Class Summary — the structured operational record of every class." },
    roles: ["Teacher", "Student", "Principal", "Parent"],
    visual: capVisuals.examination,
  },
  {
    key: "communication", icon: Mail, tone: "#5B7CFA", label: "Communication",
    tagline: "Structured, role-based communication for the whole school.",
    ops: ["Role-based channels", "Notices & circulars", "Escalations & records", "Parent-teacher exchanges", "School-wide messages"],
    primitive: { name: "PCC", full: "PARTECON Communication Channel — one governed communication layer across the school." },
    roles: ["Teacher", "Parent", "Student", "Principal", "Administration"],
    visual: capVisuals.pcc,
  },
  {
    key: "intelligence", icon: Activity, tone: "#22C55E", label: "Intelligence",
    tagline: "Daily signals turned into early, actionable insight.",
    ops: ["Performance analytics", "Behaviour & engagement", "At-risk identification", "Attendance trends", "Class & teacher patterns"],
    primitive: { name: "AHI", full: "Analytical Health Index — an analytical view of school, class, teacher and student health." },
    roles: ["Teacher", "Student", "Class", "School"],
    visual: capVisuals.ahi,
  },
  {
    key: "operations", icon: LayoutGrid, tone: "#8B5CF6", label: "School Operations",
    tagline: "The coordinating layer for daily school operations.",
    ops: ["Scheduling & calendar", "Records & workflows", "Roles & permissions", "Notifications", "Institutional coordination"],
    primitive: { name: "One Layer", full: "One governed operating layer — not five disconnected systems." },
    roles: ["Administration", "Principal"],
    visual: capVisuals.analytics,
  },
];

const PillarsExplorer = () => {
  const [active, setActive] = useState("academic");
  const p = pillarsData.find((x) => x.key === active);
  return (
    <section className="chrome py-20 md:py-28 relative overflow-hidden" data-testid="offer-pillars">
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <span className="overline overline-light">The Operating Pillars</span>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-white leading-tight">Four pillars. Three primitives. One operating layer.</h2>
          <p className="text-white/55 text-base md:text-lg leading-relaxed mt-5">Instead of adding another app to the pile, PARTECON consolidates the school's daily functions into four connected pillars — held together by three architectural primitives: <span className="text-white">EOC</span>, <span className="text-white">PCC</span> and <span className="text-white">AHI</span>.</p>
        </Reveal>
        <div className="flex flex-wrap gap-2 mt-10">
          {pillarsData.map((x) => (
            <button key={x.key} onClick={() => setActive(x.key)} data-testid={`offer-pillar-${x.key}`}
              className={`inline-flex items-center gap-2 text-[13px] font-medium rounded-lg px-4 py-2.5 border transition-colors ${active === x.key ? "bg-[#6366F1] text-white border-[#6366F1]" : "bg-white/5 text-white/60 border-white/10 hover:text-white"}`}>
              <x.icon size={15} /> {x.label}
            </button>
          ))}
        </div>
        <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
          className="grid lg:grid-cols-[1.05fr_1fr] gap-8 mt-8 items-center">
          <div className="glass rounded-2xl p-7">
            <span className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: `${p.tone}22` }}>
              <p.icon size={22} style={{ color: p.tone }} />
            </span>
            <h3 className="font-display text-2xl font-semibold text-white mt-5">{p.label}</h3>
            <p className="text-white/60 text-[15px] leading-relaxed mt-3">{p.tagline}</p>
            <div className="grid sm:grid-cols-2 gap-2.5 mt-6">
              {p.ops.map((o) => (
                <div key={o} className="flex items-center gap-2.5 text-[13px] text-white/85">
                  <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: p.tone }} />{o}
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="text-[10px] font-mono-accent uppercase tracking-widest text-white/40 mb-2">Architectural primitive</div>
              <div className="text-[14px] text-white font-medium">{p.primitive.name} <span className="text-[#A5B4FC]">·</span> <span className="text-white/70 font-normal">{p.primitive.full}</span></div>
            </div>
            <div className="mt-5">
              <div className="text-[10px] font-mono-accent uppercase tracking-widest text-white/40 mb-2">Operates across</div>
              <div className="flex flex-wrap gap-2">
                {p.roles.map((r) => <span key={r} className="text-[12px] px-3 py-1.5 rounded-full bg-white/8 text-white/80 border border-white/10">{r}</span>)}
              </div>
            </div>
          </div>
          <div>{p.visual}</div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---- Before / With: the operational transformation ---- */
const beforePoints = [
  "Fragmented systems across departments",
  "Scattered, informal communication",
  "Manual reporting & follow-ups",
  "Information arrives late",
  "Limited view of daily operations",
  "Management reacts after problems emerge",
];

const withPoints = [
  "One connected ecosystem — less fragmentation across people, departments and tools",
  "Teachers, parents, students and administration connected in the same environment",
  "Teachers spend less time on administrative work — more time teaching",
  "Leadership gets a clearer, more timely view of the whole school",
  "Issues and pending work surface earlier — not after the fact",
  "Standardised workflows bring consistency and accountability",
];

const BeforeAfter = () => (
  <section className="chrome py-20 md:py-28 relative overflow-hidden" data-testid="offer-before-after">
    <div className="orb orb-green animate-drift-slow -bottom-40 -right-32 h-[460px] w-[460px]" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal className="text-center max-w-3xl mx-auto">
        <span className="overline overline-light">The Transformation</span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 text-white leading-tight">
          From fragmented school operations to one connected operating layer.
        </h2>
        <p className="text-white/55 text-base md:text-lg leading-relaxed mt-5">
          Better visibility. Better coordination. Better execution. Better decision-making.
        </p>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6 mt-12">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
          <span className="text-[12px] font-mono-accent uppercase tracking-widest text-white/40">Before PARTECON</span>
          <ul className="mt-5 space-y-3.5">
            {beforePoints.map((t) => (
              <li key={t} className="flex items-center gap-3 text-white/55 text-[15px]"><span className="h-1.5 w-1.5 rounded-full bg-white/25 shrink-0" />{t}</li>
            ))}
          </ul>
        </div>
        <div className="relative rounded-2xl bg-white/[0.05] border border-[#6366F1]/30 p-7">
          <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r from-[#6366F1] to-[#22C55E]" />
          <span className="text-[12px] font-mono-accent uppercase tracking-widest text-[#A5B4FC]">With PARTECON</span>
          <ul className="mt-5 space-y-3.5">
            {withPoints.map((t) => (
              <li key={t} className="flex items-start gap-3 text-white text-[15px] font-medium"><span className="h-5 w-5 rounded-full bg-[#6366F1]/25 flex items-center justify-center shrink-0 mt-0.5"><Check size={12} className="text-[#A5B4FC]" /></span>{t}</li>
            ))}
          </ul>
        </div>
      </div>
      <Reveal className="text-center mt-12">
        <p className="font-display text-xl md:text-2xl font-semibold text-white leading-snug">
          Easier to operate. Easier to understand. <span className="text-[#A5B4FC]">Easier to manage.</span>
        </p>
        <p className="text-white/45 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
          And because everything runs on one operating layer, the school gains a digital foundation that
          grows with it — toward automation and intelligence, at its own pace.
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---- Final statement ---- */
const FinalStatement = () => (
  <section className="chrome py-24 md:py-32 relative overflow-hidden" data-testid="offer-final-statement">
    <div className="absolute inset-0 radial-glow" />
    <div className="orb orb-indigo animate-drift -top-24 left-1/4 h-[420px] w-[420px]" />
    <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] text-white">
          One school.<br />One operating layer.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A5B4FC] to-[#818CF8]">Every stakeholder connected.</span>
        </h2>
        <p className="text-white/60 text-lg mt-7 max-w-xl mx-auto leading-relaxed">
          PARTECON brings classroom execution, school operations, communication, performance data and
          leadership visibility into one connected ecosystem — built to evolve toward automation,
          AI intelligence and smart school infrastructure.
        </p>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 text-white/70 text-sm font-mono-accent">
          {["Teachers operate", "Students participate", "Parents stay connected", "Principals see", "Administrators manage"].map((t) => (
            <span key={t} className="flex items-center gap-2"><HealthDot /> {t}</span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Platform() {
  return (
    <>
      <PageHero
        overline="What PARTECON Offers"
        title="What PARTECON puts in one operating layer."
        sub="PARTECON connects teachers, students, parents, school leadership and administration through one real-time operating layer — bringing daily operations, communication, academic information, performance data and decision-making into one connected ecosystem."
      />

      {/* Not a collection of apps */}
      <section className="chrome py-16 border-b border-white/8" data-testid="offer-core-message">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <p className="font-display text-2xl md:text-3xl font-semibold text-white leading-snug">
            PARTECON is not a collection of apps.<br />
            <span className="text-[#A5B4FC]">It is one connected School Operating System.</span>
          </p>
          <div className="flex flex-wrap justify-center gap-2.5 mt-8">
            {["Academic Execution", "Communication", "Intelligence", "School Operations"].map((t) => (
              <span key={t} className="glass rounded-full px-4 py-2 text-[13px] text-white/85 font-medium">{t}</span>
            ))}
          </div>
        </div>
      </section>

      <StakeholderExplorer />
      <PillarsExplorer />
      <BeforeAfter />
      <FinalStatement />
    </>
  );
}
